<template>
  <main class="min-h-screen bg-white font-sans antialiased text-gray-900 selection:bg-orange-100 selection:text-orange-700">

        <landing-header />

    <!-- Progress bar -->
    <div class="fixed top-0 left-0 right-0 z-50 h-1 bg-gray-200 pointer-events-none mt-[80px]">
      <div class="h-full bg-gradient-to-r from-orange-500 to-red-500 transition-all duration-100" :style="`width:${progress}%`"></div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="min-h-screen flex items-center justify-center">
      <div class="text-center">
        <i class="fa-solid fa-circle-notch fa-spin text-orange-500 text-4xl mb-4"></i>
        <p class="text-gray-400 font-medium">Chargement de l'article…</p>
      </div>
    </div>

    <!-- 404 -->
    <div v-else-if="!article" class="min-h-screen flex items-center justify-center">
      <div class="text-center py-24">
        <div class="w-24 h-24 bg-orange-50 rounded-3xl flex items-center justify-center mx-auto mb-6 text-4xl text-orange-300">
          <i class="fa-solid fa-file-circle-question"></i>
        </div>
        <h1 class="text-3xl font-black text-gray-800 mb-4">Article introuvable</h1>
        <p class="text-gray-400 mb-8">Cet article n'existe pas ou a été supprimé.</p>
        <router-link to="/blog" class="bg-orange-500 text-white px-8 py-3 rounded-xl font-black hover:bg-orange-600 transition-colors">
          ← Retour au blog
        </router-link>
      </div>
    </div>

    <template v-else>

      <!-- HERO -->
      <section class="relative bg-gray-900 overflow-hidden pt-[80px]">
        <div class="absolute inset-0">
          <img :src="article.image_url" :alt="article.title" class="w-full h-full object-cover opacity-25" />
          <div class="absolute inset-0 bg-gradient-to-b from-gray-900/50 via-gray-900/75 to-gray-900"></div>
        </div>

        <!-- Breadcrumb -->
        <div class="relative z-10 pt-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <nav class="flex items-center gap-2 text-sm text-gray-400 flex-wrap">
            <router-link to="/" class="hover:text-orange-400 transition-colors font-medium">Accueil</router-link>
            <i class="fa-solid fa-chevron-right text-[9px] text-gray-600"></i>
            <router-link to="/blog" class="hover:text-orange-400 transition-colors font-medium">Blog</router-link>
            <i class="fa-solid fa-chevron-right text-[9px] text-gray-600"></i>
            <span class="text-gray-300 font-medium truncate max-w-xs">{{ article.title }}</span>
          </nav>
        </div>

        <div class="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-24">
          <div class="flex flex-wrap items-center gap-3 mb-8">
            <span class="bg-orange-500 text-white text-xs font-black uppercase tracking-widest px-3 py-1.5 rounded-full">Réglementation CVC</span>
            <span class="text-gray-400 text-sm font-medium flex items-center gap-1.5">
              <i class="fa-regular fa-calendar"></i> {{ formatDate(article.created_at) }}
            </span>
            <span class="text-gray-400 text-sm font-medium flex items-center gap-1.5">
              <i class="fa-regular fa-clock"></i> {{ readTime }} min de lecture
            </span>
          </div>
          <h1 class="text-4xl lg:text-6xl font-black text-white leading-[1.08] mb-8">{{ article.title }}</h1>
          <div class="flex items-center gap-4">
            <div class="w-12 h-12 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center text-white font-black text-lg">T</div>
            <div>
              <p class="text-white font-bold">Équipe Termoflow</p>
              <p class="text-gray-400 text-sm">Experts CVC & réglementation</p>
            </div>
            <!-- Share -->
            <div class="ml-auto flex items-center gap-2">
              <button @click="share('linkedin')" title="LinkedIn" class="w-9 h-9 rounded-xl bg-white/10 hover:bg-orange-500 flex items-center justify-center text-white transition-all hover:scale-110">
                <i class="fa-brands fa-linkedin-in text-sm"></i>
              </button>
              <button @click="share('twitter')" title="X" class="w-9 h-9 rounded-xl bg-white/10 hover:bg-orange-500 flex items-center justify-center text-white transition-all hover:scale-110">
                <i class="fa-brands fa-x-twitter text-sm"></i>
              </button>
              <button @click="copyLink" title="Copier le lien" class="w-9 h-9 rounded-xl bg-white/10 hover:bg-orange-500 flex items-center justify-center text-white transition-all hover:scale-110">
                <i :class="copied ? 'fa-solid fa-check' : 'fa-solid fa-link'" class="text-sm"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- CONTENT + SIDEBAR -->
      <section class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div class="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-16">

          <!-- Article body -->
          <article>
            <div class="blog-content" v-html="article.content"></div>

            <!-- Footer actions -->
            <div class="mt-16 pt-10 border-t border-gray-100 flex flex-wrap items-center justify-between gap-6">
              <div class="flex flex-wrap gap-2">
                <span class="px-4 py-2 bg-gray-100 rounded-xl text-sm font-bold text-gray-500">#réglementation</span>
                <span class="px-4 py-2 bg-gray-100 rounded-xl text-sm font-bold text-gray-500">#chaudière</span>
                <span class="px-4 py-2 bg-gray-100 rounded-xl text-sm font-bold text-gray-500">#CVC</span>
              </div>
              <div class="flex items-center gap-3">
                <span class="text-sm font-bold text-gray-400">Partager :</span>
                <button @click="share('linkedin')" class="w-10 h-10 rounded-xl border-2 border-gray-200 hover:border-orange-500 hover:bg-orange-500 hover:text-white flex items-center justify-center text-gray-500 transition-all">
                  <i class="fa-brands fa-linkedin-in text-sm"></i>
                </button>
                <button @click="share('twitter')" class="w-10 h-10 rounded-xl border-2 border-gray-200 hover:border-orange-500 hover:bg-orange-500 hover:text-white flex items-center justify-center text-gray-500 transition-all">
                  <i class="fa-brands fa-x-twitter text-sm"></i>
                </button>
                <button @click="copyLink" class="w-10 h-10 rounded-xl border-2 border-gray-200 hover:border-orange-500 hover:bg-orange-500 hover:text-white flex items-center justify-center text-gray-500 transition-all">
                  <i :class="copied ? 'fa-solid fa-check text-green-500' : 'fa-solid fa-link'" class="text-sm"></i>
                </button>
              </div>
            </div>

            <!-- Author card -->
            <div class="mt-12 bg-gray-50 rounded-3xl p-8 flex gap-6 items-start border border-gray-100">
              <div class="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center text-white text-2xl font-black flex-shrink-0">T</div>
              <div>
                <p class="font-black text-gray-900 mb-1">Équipe Termoflow</p>
                <p class="text-orange-600 text-sm font-bold mb-3">Experts CVC & conformité réglementaire</p>
                <p class="text-gray-500 text-sm leading-relaxed">Notre équipe regroupe des experts en réglementation CVC, F-Gaz et gestion d'entreprise pour artisans du chauffage et de la climatisation.</p>
              </div>
            </div>
          </article>

          <!-- Sidebar -->
          <aside>
            <div class="sticky top-[100px] space-y-6">

              <!-- TOC -->
              <div v-if="toc.length > 0" class="bg-gray-50 rounded-3xl p-7 border border-gray-100">
                <h3 class="text-xs font-black uppercase tracking-widest text-gray-400 mb-5">Dans cet article</h3>
                <nav class="space-y-1">
                  <a
                    v-for="item in toc" :key="item.id"
                    :href="`#${item.id}`"
                    @click.prevent="scrollTo(item.id)"
                    :class="[
                      'flex items-start gap-2.5 py-2 px-3 rounded-xl text-sm transition-all group cursor-pointer',
                      item.level === 3 ? 'pl-7' : '',
                      activeSection === item.id
                        ? 'bg-orange-500 text-white font-bold'
                        : 'text-gray-600 hover:bg-white hover:text-orange-600 font-medium'
                    ]"
                  >
                    <i :class="['fa-solid mt-0.5 flex-shrink-0 text-xs', activeSection === item.id ? 'fa-circle-dot' : 'fa-circle text-gray-300 group-hover:text-orange-400']"></i>
                    {{ item.text }}
                  </a>
                </nav>
              </div>

              <!-- CTA -->
              <div class="bg-gradient-to-br from-orange-500 to-red-500 rounded-3xl p-7 text-white relative overflow-hidden">
                <div class="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
                <div class="relative z-10">
                  <div class="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center mb-5">
                    <i class="fa-solid fa-rocket text-xl"></i>
                  </div>
                  <h3 class="font-black text-xl mb-3 leading-tight">Gérez votre conformité sans paperasse</h3>
                  <p class="text-orange-100 text-sm leading-relaxed mb-6">Termoflow automatise vos attestations, CERFA et tournées de maintenance.</p>
                  <a href="https://termoflow.fr" class="block w-full bg-white text-orange-600 py-3 px-5 rounded-xl font-black text-center text-sm hover:bg-orange-50 transition-colors">
                    Essai gratuit →
                  </a>
                </div>
              </div>

              <!-- Lire aussi -->
              <div v-if="related.length > 0" class="bg-white border border-gray-100 rounded-3xl p-7">
                <h3 class="text-xs font-black uppercase tracking-widest text-gray-400 mb-5">Lire aussi</h3>
                <div class="space-y-5">
                  <router-link v-for="r in related.slice(0,3)" :key="r.id" :to="`/blog/${r.slug}`" class="flex gap-4 group">
                    <img :src="r.image_url" :alt="r.title" class="w-16 h-16 rounded-xl object-cover flex-shrink-0 group-hover:opacity-80 transition-opacity" />
                    <div class="min-w-0">
                      <p class="text-xs text-gray-400 mb-1">{{ formatDate(r.created_at) }}</p>
                      <p class="text-sm font-bold text-gray-800 group-hover:text-orange-600 transition-colors leading-snug line-clamp-2">{{ r.title }}</p>
                    </div>
                  </router-link>
                </div>
              </div>

            </div>
          </aside>
        </div>
      </section>

      <!-- ARTICLES SIMILAIRES -->
      <section v-if="related.length > 0" class="bg-gray-50 border-t border-gray-100 py-20">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div class="flex items-end justify-between mb-12">
            <div>
              <p class="text-orange-600 font-black uppercase tracking-widest text-xs mb-2">Continuer la lecture</p>
              <h2 class="text-4xl font-black text-gray-900">Articles similaires</h2>
            </div>
            <router-link to="/blog" class="hidden sm:flex items-center gap-2 text-sm font-bold text-gray-500 hover:text-orange-600 transition-colors">
              Voir tout <i class="fa-solid fa-arrow-right text-xs"></i>
            </router-link>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <router-link v-for="r in related" :key="r.id" :to="`/blog/${r.slug}`" class="group bg-white rounded-[1.75rem] border border-gray-100 overflow-hidden hover:shadow-xl hover:shadow-gray-200/60 hover:-translate-y-1.5 transition-all duration-300">
              <div class="relative overflow-hidden aspect-[16/9]">
                <img :src="r.image_url" :alt="r.title" class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-600" />
                <div class="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                <div class="absolute bottom-3 right-3 bg-black/60 backdrop-blur-sm text-white text-xs font-bold px-2.5 py-1 rounded-lg">
                  <i class="fa-regular fa-clock mr-1"></i>{{ readTimeFor(r.content) }} min
                </div>
              </div>
              <div class="p-7">
                <div class="flex items-center gap-2 mb-4">
                  <span class="text-xs font-black uppercase tracking-wider text-orange-600">CVC</span>
                  <span class="w-1 h-1 rounded-full bg-gray-200"></span>
                  <span class="text-xs text-gray-400">{{ formatDate(r.created_at) }}</span>
                </div>
                <h3 class="font-black text-gray-900 text-lg leading-tight mb-3 group-hover:text-orange-600 transition-colors line-clamp-2">{{ r.title }}</h3>
                <p class="text-sm text-gray-400 leading-relaxed line-clamp-2 mb-6">{{ stripHtml(r.content) }}</p>
                <div class="pt-4 border-t border-gray-100 flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <div class="w-7 h-7 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center text-white text-xs font-black">T</div>
                    <span class="text-xs font-bold text-gray-500">Équipe Termoflow</span>
                  </div>
                  <span class="text-xs font-bold text-orange-500 flex items-center gap-1.5 group-hover:gap-2.5 transition-all">
                    Lire <i class="fa-solid fa-arrow-right text-[10px]"></i>
                  </span>
                </div>
              </div>
            </router-link>
          </div>
        </div>
      </section>

      <!-- CTA FINAL -->
      <section class="py-28 relative overflow-hidden bg-gray-900">
        <div class="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
        <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl h-96 bg-gradient-to-r from-orange-600/30 to-red-600/30 blur-[100px] rounded-full pointer-events-none"></div>
        <div class="max-w-4xl mx-auto px-4 text-center relative z-10">
          <h2 class="text-5xl lg:text-6xl font-black text-white mb-6">
            Prêt à digitaliser<br>
            <span class="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-500">votre gestion CVC ?</span>
          </h2>
          <p class="text-xl text-gray-300 mb-12 max-w-2xl mx-auto leading-relaxed">Termoflow gère vos contrats, vos attestations et vos tournées. Vous, vous faites le terrain.</p>
          <div class="flex flex-col sm:flex-row justify-center gap-4">
            <a href="https://termoflow.fr/register" class="bg-orange-500 hover:bg-orange-400 text-white px-12 py-5 rounded-2xl font-black text-xl shadow-2xl shadow-orange-600/40 hover:scale-105 transition-all duration-300">Démarrer l'essai gratuit</a>
            <a href="https://termoflow.fr/contact" class="bg-transparent border-2 border-gray-600 text-white px-12 py-5 rounded-2xl font-black text-xl hover:bg-white hover:text-gray-900 transition-all duration-300">Parler à un expert</a>
          </div>
        </div>
      </section>

    </template>

    <landing-footer />
  </main>
</template>

<script>
import LandingHeader from './LandingHeader.vue'
import LandingFooter from './LandingFooter.vue'

export default {
  name: 'BlogArticle',
    components: { LandingHeader, LandingFooter },
  data() {
    return {
      article: null,
      related: [],
      loading: true,
      toc: [],
      activeSection: null,
      progress: 0,
      copied: false,
      observer: null,
    }
  },
  computed: {
    readTime() {
      if (!this.article) return 1
      return Math.max(1, Math.ceil(this.stripHtml(this.article.content).split(/\s+/).length / 200))
    },
  },
  watch: {
    '$route.params.slug'(slug) { if (slug) this.load(slug) },
  },
  mounted() {
    this.load(this.$route.params.slug)
    window.addEventListener('scroll', this.onScroll)
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.onScroll)
    this.observer?.disconnect()
  },
  methods: {
    async load(slug) {
      this.loading = true
      this.toc     = []
      this.observer?.disconnect()
      try {
        const response = await this.$request.get(`/blog/${slug}`)
        this.article = response.article || response
        this.related = response.related || []
        await this.$nextTick()
        this.buildToc()
      } catch {
        this.article = null
      } finally {
        this.loading = false
      }
    },
    buildToc() {
      const el = this.$el.querySelector('.blog-content')
      if (!el) return
      const headings = el.querySelectorAll('h2, h3')
      this.toc = Array.from(headings).map((h, i) => {
        h.id = `section-${i}`
        return { id: `section-${i}`, text: h.textContent.trim(), level: parseInt(h.tagName[1]) }
      })
      if (this.toc.length) this.activeSection = this.toc[0].id

      this.observer = new IntersectionObserver(
        entries => entries.forEach(e => { if (e.isIntersecting) this.activeSection = e.target.id }),
        { rootMargin: '-15% 0px -70% 0px' }
      )
      this.toc.forEach(item => { const el = document.getElementById(item.id); if (el) this.observer.observe(el) })
    },
    scrollTo(id) {
      const el = document.getElementById(id)
      if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 80, behavior: 'smooth' })
    },
    onScroll() {
      const doc = document.documentElement.scrollHeight - window.innerHeight
      this.progress = doc > 0 ? Math.min(100, (window.scrollY / doc) * 100) : 0
    },
    share(platform) {
      const url   = encodeURIComponent(window.location.href)
      const title = encodeURIComponent(this.article?.title || '')
      const urls  = { linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`, twitter: `https://twitter.com/intent/tweet?url=${url}&text=${title}` }
      if (urls[platform]) window.open(urls[platform], '_blank', 'width=600,height=400')
    },
    async copyLink() {
      try { await navigator.clipboard.writeText(window.location.href); this.copied = true; setTimeout(() => this.copied = false, 2000) } catch {}
    },
    stripHtml(html = '') { return new DOMParser().parseFromString(html, 'text/html').body.textContent?.trim().slice(0, 220) || '' },
    readTimeFor(c = '')  { return Math.max(1, Math.ceil(this.stripHtml(c).split(/\s+/).length / 200)) },
    formatDate(d)        { return d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '' },
  },
}
</script>

<style scoped>
.blog-content .bg-orange-50, .blog-content .bg-orange-100, .blog-content .bg-orange-200, .blog-content .bg-orange-300, .blog-content .bg-orange-400, .blog-content .bg-orange-500, .blog-content .bg-orange-600, .blog-content .bg-orange-700 { color: white !important }
.blog-content { font-size:1.0625rem; line-height:1.85; }
.blog-content h1 { font-size:2.25rem; font-weight:900; margin-top:3rem; margin-bottom:1.25rem; line-height:1.15 }
.blog-content h2 { font-size:1.75rem; font-weight:900; margin-top:3rem; margin-bottom:1rem; padding-bottom:.6rem; border-bottom:3px solid #fed7aa }
.blog-content h3 { font-size:1.25rem; font-weight:800; margin-top:2rem; margin-bottom:.75rem }
.blog-content p  { margin-bottom:1.5rem }
.blog-content strong { font-weight:800; }
.blog-content a  { color:#f97316; font-weight:700; text-decoration:underline; text-decoration-color:#fed7aa; text-underline-offset:3px; transition:color .2s }
.blog-content a:hover { color:#ea580c }
.blog-content ul { list-style:none; padding:0; margin-bottom:1.5rem }
.blog-content ul li { padding-left:1.75rem; position:relative; margin-bottom:.6rem; color:#4b5563 }
.blog-content ul li::before { content:''; position:absolute; left:0; top:.65em; width:8px; height:8px; background:#f97316; border-radius:50% }
.blog-content ol { list-style:decimal; padding-left:1.5rem; margin-bottom:1.5rem }
.blog-content ol li { margin-bottom:.5rem; color:#4b5563 }
.blog-content blockquote { border-left:4px solid #f97316; background:#fff7ed; padding:1rem 1.5rem; border-radius:0 1rem 1rem 0; margin:2rem 0; font-style:italic; color:#92400e }
.line-clamp-2 { display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden }
.line-clamp-3 { display:-webkit-box; -webkit-line-clamp:3; -webkit-box-orient:vertical; overflow:hidden }
</style>